import axios from 'axios';
import * as Sentry from '@sentry/react';

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    timeout: 12000, // 12 seconds global timeout
    headers: {
        'Content-Type': 'application/json',
    },
});

// Deduplication flag: prevents multiple simultaneous 401 responses
// from each triggering token removal + redirect independently.
// Must be reset when the user logs in again (see resetAuthInterceptor).
let isLoggingOut = false;

/**
 * Reset the 401 deduplication flag.
 * Call this after a successful login so that future 401s are handled
 * correctly — even without a full page reload (PWA scenario).
 */
export function resetAuthInterceptor() {
    isLoggingOut = false;
}

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token && config.headers) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Catch network timeouts or connection drops
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            Sentry.captureMessage(`API Timeout silencioso: ${error.config?.url || 'Unknown URL'}`, 'warning');
        } else if (error.response && error.response.status >= 500) {
            Sentry.captureException(new Error(`Server Error ${error.response.status}: ${error.config?.url || 'Unknown URL'} ${error.response.data}`));
        }

        if (error.response && error.response.status === 401) {
            const requestUrl = error.config?.url || '';
            if (!requestUrl.includes('/api/auth/')) {
                if (!isLoggingOut) {
                    // First 401 in the cascade: clean up once
                    isLoggingOut = true;
                    localStorage.removeItem('token');
                    window.dispatchEvent(new CustomEvent('auth:logout'));
                }
                return Promise.reject(Object.assign(new Error('auth:logout'), { isAuthLogout: true }));
            }
        }
        return Promise.reject(error);
    }
);

export default api;
